import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UpgradeModal from './UpgradeModal';
import '../assets/css/mantis-layout.css';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const currentPage = location.pathname.split('/')[1] || 'dashboard';
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Fehler beim Ausloggen:', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (!user) {
    return null;
  }

  // Navigation structure like Mantis Template
  const navigationGroups = [
    {
      id: 'navigation',
      title: 'Navigation',
      type: 'group',
      children: [
        {
          id: 'dashboard',
          title: 'Dashboard',
          type: 'item',
          url: '/dashboard',
          icon: 'dashboard',
          breadcrumbs: false
        }
      ]
    },
    {
      id: 'management',
      title: 'Management',
      type: 'group',
      children: [
        {
          id: 'direkterzeuger-admin',
          title: 'Direkterzeuger',
          type: 'item',
          url: '/direkterzeuger-admin',
          icon: 'agriculture',
          breadcrumbs: false
        },
        {
          id: 'user-suggestions',
          title: 'User Suggestions',
          type: 'item',
          url: '/user-suggestions',
          icon: 'feedback',
          breadcrumbs: false
        },
        {
          id: 'tasks',
          title: 'Aufgabenliste',
          type: 'item',
          url: '/tasks',
          icon: 'task_alt',
          breadcrumbs: false
        }
      ]
    },
    {
      id: 'feedback',
      title: 'Feedback',
      type: 'group',
      children: [
        {
          id: 'feedback-producer',
          title: 'Producer',
          type: 'item',
          url: '/feedback/producer',
          icon: 'feedback',
          breadcrumbs: false
        }
      ]
    }
  ];

  // Breadcrumb logic
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs = [{ label: 'Dashboard', path: '/dashboard' }];
    
    if (pathSegments.length > 0 && pathSegments[0] !== 'dashboard') {
      // Find current item in navigation
      for (const group of navigationGroups) {
        for (const item of group.children) {
          if (item.url === `/${pathSegments[0]}`) {
            breadcrumbs.push({ label: item.title, path: item.url });
            break;
          }
        }
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Get page title based on current route
  const getPageTitle = () => {
    const pageMap = {
      'dashboard': 'Dashboard',
      'direkterzeuger-admin': 'Direkterzeuger',
      'user-suggestions': 'User Suggestions',
      'tasks': 'Aufgabenliste',
      'feedback': 'Feedback',
      'feedback-producer': 'Producer Feedback'
    };
    return pageMap[currentPage] || 'Dashboard';
  };

  return (
    <div className="mantis-layout">
      {/* Sidebar */}
      <aside className={`mantis-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <i className="material-icons">eco</i>
            {!isSidebarCollapsed && <span>Local & Fresh</span>}
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {navigationGroups.map((group) => (
            <div key={group.id} className="nav-group">
              {!isSidebarCollapsed && (
                <div className="nav-group-title">
                  {group.title}
                </div>
              )}
              <div className="nav-group-items">
                {group.children.map((item) => (
                  <Link
                    key={item.id}
                    to={item.url}
                    className={`nav-item ${currentPage === item.url.slice(1) ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <i className="material-icons nav-icon">{item.icon}</i>
                    {!isSidebarCollapsed && <span className="nav-text">{item.title}</span>}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <div className={`mobile-sidebar-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>
      <aside className={`mantis-sidebar mobile-sidebar ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <i className="material-icons">eco</i>
            <span>Local & Fresh</span>
          </div>
          <button className="close-mobile-sidebar" onClick={closeMobileMenu}>
            <i className="material-icons">close</i>
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {navigationGroups.map((group) => (
            <div key={group.id} className="nav-group">
              <div className="nav-group-title">
                {group.title}
              </div>
              <div className="nav-group-items">
                {group.children.map((item) => (
                  <Link
                    key={item.id}
                    to={item.url}
                    className={`nav-item ${currentPage === item.url.slice(1) ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <i className="material-icons nav-icon">{item.icon}</i>
                    <span className="nav-text">{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={`mantis-main ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Top Header */}
        <header className="mantis-header">
          <div className="header-left">
            <button className="sidebar-toggle desktop-toggle" onClick={toggleSidebar}>
              <i className="material-icons">menu</i>
            </button>
            <button className="sidebar-toggle mobile-toggle" onClick={toggleMobileMenu}>
              <i className="material-icons">menu</i>
            </button>
            
            {/* Page Header */}
            <div className="page-header">
              <p className="date">{new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
              <h2>{getPageTitle()}</h2>
            </div>
          </div>
          
          <div className="header-right">
            {/* Hofauswahl und Membership-Anzeige entfernt */}
          </div>
        </header>

        {/* Content Area */}
        <main className="mantis-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;